// A value the app learns once, some time after it starts (the site's CRM,
// from /api/me), and work that must wait for it: run now if it is known,
// else when it first becomes known. Pure apart from the work it runs.

/** Stop waiting: work not yet run never runs. */
export type Cancel = () => void;

export interface OnceKnown<T> {
  /** The value has become known (or changed): run everything waiting for it. */
  set(value: T): void;
  /**
   * Run `work` with the value now if it is known, else once, when it is.
   * The returned Cancel stops a wait — a React effect returns it, so a
   * component mounted twice (StrictMode) or gone before the answer runs it once at most.
   */
  whenKnown(work: (value: T) => void): Cancel;
}

const NOTHING_TO_CANCEL: Cancel = () => undefined;

export function createOnceKnown<T>(): OnceKnown<T> {
  let known: { value: T } | null = null;
  let waiting: ((value: T) => void)[] = [];
  return {
    set(value) {
      known = { value };
      const ready = waiting;
      waiting = [];
      for (const work of ready) work(value);
    },
    whenKnown(work) {
      if (known) {
        work(known.value);
        return NOTHING_TO_CANCEL;
      }
      waiting.push(work);
      return () => {
        waiting = waiting.filter((w) => w !== work);
      };
    },
  };
}
